import { useState, useEffect } from "react";
import { Lock, Clock, User, Crown } from "lucide-react";
import { AuthModal } from "./AuthModal";
import type { Session } from "@supabase/supabase-js";
import type { UserProfile } from "../types";
import { supabase } from "../supabase";
import { getHardwareId } from "../utils/device";

const TRIAL_DAYS = 3;
const STORAGE_KEY = "trial_access";

interface TrialGateProps {
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  session: Session | null;
  onUnlock: () => void;
  onSession: (s: Session) => void;
  onProfileSaved?: (p: UserProfile) => void;
  onSubscribe: () => void;
}

interface TrialData {
  active: boolean;
  daysLeft: number;
  expired: boolean;
  permanent: boolean;
}

function getLocalTrialInfo(): TrialData {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { active: false, daysLeft: 0, expired: false, permanent: false };
  try {
    const data = JSON.parse(raw);
    if (data.permanent) return { active: true, daysLeft: 0, expired: false, permanent: true };
    const start = new Date(data.startDate).getTime();
    const elapsed = Date.now() - start;
    const daysElapsed = Math.floor(elapsed / (1000 * 60 * 60 * 24));
    const daysLeft = TRIAL_DAYS - daysElapsed;
    return { active: daysLeft > 0, daysLeft, expired: daysLeft <= 0, permanent: false };
  } catch {
    return { active: false, daysLeft: 0, expired: false, permanent: false };
  }
}

function grantPermanent() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ permanent: true, startDate: new Date().toISOString() }));
}

export function TrialGate({ t, dir, session, onUnlock, onSession, onProfileSaved, onSubscribe }: TrialGateProps) {
  const [showAuth, setShowAuth] = useState(false);
  const [trialData, setTrialData] = useState<TrialData | null>(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    if (session) {
      grantPermanent();
      onUnlock();
      return;
    }

    let cancelled = false;

    (async () => {
      const localInfo = getLocalTrialInfo();
      if (localInfo.permanent) {
        if (!cancelled) { setTrialData(localInfo); setLoading(false); onUnlock(); }
        return;
      }

      try {
        const hwId = await getHardwareId();
        const { data, error } = await supabase.rpc("start_or_get_trial", { p_device_id: hwId });
        if (cancelled) return;

        if (error || !data || data.length === 0) {
          if (localInfo.active) { onUnlock(); }
          setTrialData(localInfo);
          setLoading(false);
          return;
        }

        const row = data[0];
        const cloudInfo: TrialData = {
          active: row.is_active,
          daysLeft: row.days_left,
          expired: row.expired,
          permanent: false,
        };

        if (cloudInfo.active && !localInfo.permanent) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ startDate: row.started_at }));
          onUnlock();
        }
        setTrialData(cloudInfo);
      } catch {
        if (cancelled) return;
        if (localInfo.active) { onUnlock(); }
        setTrialData(localInfo);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const info = trialData ?? getLocalTrialInfo();

  if (session || info.permanent || info.active) {
    return null;
  }

  return (
    <>
      {!showAuth && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#0D1B2E] dark:bg-[#050E1F]" dir={dir}>
        <div className="w-full max-w-sm bg-white/95 dark:bg-slate-900/95 rounded-3xl shadow-2xl border border-cyan-500/20 overflow-hidden">
          <div className="bg-gradient-to-br from-[#0A2240] to-[#0D1B2E] px-6 py-8 flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
              <Lock className="w-7 h-7 text-cyan-300" />
            </div>
            <h1 className="font-bold text-xl text-cyan-300">{t("trialExpired")}</h1>
            <p className="text-sm text-cyan-100/70 text-center leading-relaxed">{t("trialSignInSubtitle")}</p>
          </div>
          <div className="px-6 py-6 space-y-3">
            {!info.active && !info.permanent && !info.expired && (
              <button
                onClick={async () => {
                  try {
                    const hwId = await getHardwareId();
                    await supabase.rpc("start_or_get_trial", { p_device_id: hwId });
                  } catch { /* fallback below */ }
                  localStorage.setItem(STORAGE_KEY, JSON.stringify({ startDate: new Date().toISOString() }));
                  onUnlock();
                }}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                {t("trialStartBtn")}
              </button>
            )}
            <button
              onClick={() => setShowAuth(true)}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
            >
              <User className="w-4 h-4" />
              {t("signIn")}
            </button>
            <button
              onClick={() => setShowAuth(true)}
              className="w-full py-3.5 rounded-xl border-2 border-cyan-500/40 text-cyan-600 dark:text-cyan-400 font-bold text-sm transition-all flex items-center justify-center gap-2 hover:bg-cyan-50 dark:hover:bg-cyan-500/10"
            >
              {t("signUp")}
            </button>
            <button
              onClick={onSubscribe}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
            >
              <Crown className="w-4 h-4" />
              {t("trialSubscribeBtn")}
            </button>
            <p className="text-xs text-stone-400 dark:text-slate-500 text-center pt-2 flex items-center justify-center gap-1.5">
              <Clock className="w-3 h-3" />
              {t("trialForgotHint")}
            </p>
          </div>
        </div>
      </div>
      )}

      <AuthModal
        open={showAuth}
        session={session}
        onClose={() => setShowAuth(false)}
        t={t}
        dir={dir}
        onSuccess={(s) => { onSession(s); grantPermanent(); onUnlock(); }}
        onProfileSaved={onProfileSaved}
        onSignOut={() => {}}
      />
    </>
  );
}

export { getLocalTrialInfo as getTrialInfo, TRIAL_DAYS, STORAGE_KEY };
